import { useState } from 'react';
import type { Movie } from '@/types';
import StarRating from '@/components/base/StarRating';
import { formatDuration } from '@/lib/data';
import { useFavorites } from '@/hooks/useFavorites';
import { buildShareUrl } from '@/lib/share';
import ShareDialog from '@/components/feature/ShareDialog';

export default function MovieHero({ movie }: { movie: Movie }) {
  const { isFavorite, toggleFavorite } = useFavorites();
  const [shareOpen, setShareOpen] = useState(false);
  const [toast, setToast] = useState('');
  const favorite = isFavorite(movie.id);
  const shareUrl = buildShareUrl(`/detail/${movie.id}`);

  const showToast = (msg: string) => {
    setToast(msg);
    window.setTimeout(() => setToast(''), 2200);
  };

  const handleFavorite = () => {
    const now = toggleFavorite(movie.id);
    showToast(now ? '已加入收藏' : '已取消收藏');
  };

  const meta = [
    { label: '导演', value: movie.director, icon: 'ri-user-star-line' },
    { label: '上映日期', value: movie.releaseDate, icon: 'ri-calendar-line' },
    { label: '影片类型', value: movie.genres.join(' / '), icon: 'ri-price-tag-3-line' },
    { label: '片长', value: formatDuration(movie.duration), icon: 'ri-time-line' },
    { label: '制片地区', value: movie.region, icon: 'ri-map-pin-line' },
    { label: '英文片名', value: movie.titleEn, icon: 'ri-translate-2' },
  ];

  return (
    <section className="rounded-lg overflow-hidden border border-background-200/80 bg-background-50">
      <div className="grid grid-cols-1 md:grid-cols-[300px_1fr] gap-0">
        <div className="relative w-full aspect-[2/3] md:aspect-auto md:h-full min-h-[320px] bg-background-200">
          <img
            src={movie.cover}
            alt={`${movie.title} 电影封面`}
            title={`${movie.title} · 韩国理论片`}
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
        </div>

        <div className="p-5 md:p-8">
          <div className="flex flex-wrap items-center gap-2">
            <span className="rounded-full bg-primary-50 px-3 py-1 font-label text-xs font-semibold text-primary-700">
              {movie.year} 年上映
            </span>
            {movie.tags.slice(0, 2).map((t) => (
              <span key={t} className="rounded-full bg-secondary-100 px-3 py-1 font-label text-xs text-secondary-900">
                {t}
              </span>
            ))}
          </div>

          <h1 className="mt-3 font-heading text-2xl md:text-4xl font-extrabold leading-tight text-foreground-950">
            {movie.title}
          </h1>
          <p className="mt-1.5 text-sm text-foreground-500">
            {movie.titleEn} · {movie.director} 导演
          </p>

          <div className="mt-3 flex items-center gap-2">
            <span className="inline-flex items-center gap-1 rounded-md bg-accent-50 px-2.5 py-1">
              <StarRating rating={movie.rating} size="md" />
            </span>
            <span className="text-xs text-foreground-500">观众综合评分（满分 10）</span>
          </div>

          <p className="mt-5 text-sm md:text-base leading-relaxed text-foreground-700">{movie.synopsis}</p>

          <div className="mt-5 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={handleFavorite}
              className={`inline-flex items-center gap-2 h-11 px-5 rounded-md font-label text-sm font-semibold transition-colors whitespace-nowrap cursor-pointer ${
                favorite
                  ? 'bg-accent-500 text-primary-950 hover:bg-accent-400'
                  : 'bg-primary-500 text-background-50 hover:bg-primary-600'
              }`}
            >
              <i className={favorite ? 'ri-heart-3-fill' : 'ri-heart-3-line'} aria-hidden="true" />
              {favorite ? '已收藏' : '收藏影片'}
            </button>
            <button
              type="button"
              onClick={() => setShareOpen(true)}
              className="inline-flex items-center gap-2 h-11 px-5 rounded-md border border-background-300 bg-background-50 font-label text-sm font-semibold text-foreground-800 hover:border-primary-300 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-share-line" aria-hidden="true" />
              分享
            </button>
          </div>

          <dl className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 border-t border-background-100 pt-5">
            {meta.map((m) => (
              <div key={m.label} className="flex items-start gap-2">
                <i className={`${m.icon} mt-0.5 text-primary-400`} aria-hidden="true" />
                <div className="min-w-0">
                  <dt className="font-label text-xs text-foreground-500">{m.label}</dt>
                  <dd className="text-sm font-medium text-foreground-900">{m.value}</dd>
                </div>
              </div>
            ))}
          </dl>

          <div className="mt-5">
            <p className="font-label text-xs text-foreground-500">主演</p>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {movie.cast.map((c) => (
                <span key={c} className="rounded-full bg-secondary-100 px-2.5 py-1 font-label text-xs text-secondary-900">
                  {c}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>

      <ShareDialog open={shareOpen} onClose={() => setShareOpen(false)} url={shareUrl} title={`《${movie.title}》- 韩国理论片`} />

      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 animate-fade-up">
          <div className="flex items-center gap-2 rounded-full bg-foreground-950 px-4 py-2.5 text-sm text-background-50">
            <i className="ri-checkbox-circle-fill text-accent-400" aria-hidden="true" />
            {toast}
          </div>
        </div>
      )}
    </section>
  );
}